/**
 * Layout da área de trabalho da sala (FC.Room.UIModule.DockSite / Interf).
 *
 * Do binário: a área de trabalho tem lugares de doca (TDockSitePlace:
 * MostLeft, Left, Top, Right, Bottom, MostRight, Client). Cada lugar guarda uma
 * árvore de nós (TDockNodeKind: grupo, conteúdo, vazio), e grupos têm orientação
 * horizontal ou vertical. Nós de conteúdo têm abas ("sheets"), cada uma com um
 * módulo identificado por classId (system/mainChat, system/stdVertUserList, …). Uma
 * aba pode ser a principal (sfMainSheet), que não pode ser fechada.
 *
 * Aqui (docs/spec/room-layout.md), a mesa é só de **abas**, que se encaixam em **espaços**: cada nó
 * de conteúdo é um espaço, sem nome e sem barra, com as abas que a pessoa quiser, de qualquer
 * módulo (a biblioteca, o chat, uma cena, uma ficha, uma tabela). No código, o espaço ainda se chama
 * painel (`ContentNode`, `closePanel`, `movePanel`). Um espaço pode ficar vazio, à espera de uma
 * aba, e pode estar recolhido (`collapsed`), só com a faixa de abas.
 *
 * Uma aba nova entra num espaço que já existe (`tabDestination`): junto das do mesmo tipo
 * (`tabKind`), senão num espaço vazio, senão no último espaço usado. Um espaço só nasce se não
 * houver nenhum. As abas mudam de espaço arrastadas, a qualquer hora (`moveSheet`), e o espaço
 * inteiro também, pela parte vazia da faixa (`movePanel`): para outro lugar, ou junto das abas de
 * outro espaço.
 *
 * Uma janela própria (docs/spec/popout-windows.md) é um pedaço desta árvore, marcado na raiz
 * (`out`): a mesa desenha sem ele, a janela desenha só ele, e dentro dela valem as mesmas operações.
 * Fechar a janela tira a marca, e tudo volta no mesmo lugar.
 *
 * O original persiste o layout no NDB da sala; aqui, por enquanto, fica por sala
 * no armazenamento local.
 *
 * No modo de edição (Ctrl+E), a pessoa põe um módulo num lugar (`placeModule`) e volta ao arranjo
 * inicial (`resetLayout`). O centro (`client`) pode ficar vazio: o chat principal pode morar numa
 * lateral, e nunca some. O "Mover para" do menu do espaço o leva para um lado da mesa
 * (`movePanelTo`).
 */

/**
 * Os módulos que esta versão conhece. Um arranjo guardado por uma versão mais nova pode trazer outro:
 * a aba dele sai ao carregar (`normalizeLayout`), em vez de a mesa não abrir.
 */
export const MODULE_CLASS_IDS = [
  'system/mainChat',
  'system/stdVertUserList',
  'system/roomLibrary',
  'system/scene',
  'system/musicPlayer',
  'system/sfxPlayer',
  // os arquivos do Desk Cloud ligados à mesa (docs/spec/desk-cloud.md)
  'system/roomFiles',
  // a aba Sessões: a sessão de jogo aberta e o registro das anteriores (docs/spec/session-clock.md)
  'system/gameSessions',
  'system/tableDock',
  'system/roomSettings',
  'system/charSheet',
  'system/pvtChat',
  'system/groupPVTChat',
] as const;
export type ModuleClassId = (typeof MODULE_CLASS_IDS)[number];
const KNOWN_MODULES: ReadonlySet<string> = new Set(MODULE_CLASS_IDS);

/** `param`: o alvo da aba quando o módulo tem várias instâncias (pvt: userId; grupo: groupId). */
export type Sheet = { id: string; classId: ModuleClassId; main?: boolean; param?: string };

/** De onde saiu a janela que nasceu de uma aba: o espaço e a posição da aba nele. */
export type Home = { nodeId: string; index: number };

/**
 * A marca da raiz de uma janela própria (docs/spec/popout-windows.md), num espaço ou num grupo:
 * `out` é o id da janela; `home`, na janela que nasceu de uma aba, o espaço para onde ela volta.
 */
type WindowMark = { out?: string; home?: Home };

/**
 * Um espaço: as abas, a que está à vista e, recolhido, só a faixa de abas. Sem abas, é o espaço
 * vazio (`active` vazio), à espera da próxima, e nunca fica recolhido.
 */
export type ContentNode = { kind: 'content'; id: string; sheets: Sheet[]; active: string; collapsed?: true } & WindowMark;

export type DockNode = ({ kind: 'group'; id: string; orientation: 'horizontal' | 'vertical'; children: DockNode[]; sizes: number[] } & WindowMark) | ContentNode;

export type DockPlace = 'mostLeft' | 'left' | 'top' | 'client' | 'bottom' | 'right' | 'mostRight';

export type DockSite = { node: DockNode; size: number };

/**
 * A versão 3 tem espaços genéricos, que podem ficar vazios (até 2026-10-29, com nome: o nome
 * guardado sai ao abrir); a 2 tinha um painel por módulo (ou por grupo de abas), e a 1 misturava
 * abas de módulos quaisquer.
 */
export type DesktopLayout = { version: 3; sites: Partial<Record<DockPlace, DockSite>> };

/** Os lados de um espaço ou da mesa. */
export type DropSide = 'left' | 'right' | 'top' | 'bottom';

/**
 * Onde um espaço (ou um módulo novo) vai parar:
 * - `split`: o espaço de destino se divide, e o novo fica do lado indicado;
 * - `edge`: uma coluna (ou faixa) nova na borda da mesa; com `win`, a borda daquela janela própria,
 *   que se divide;
 * - `center`: o centro (soltar ali só existe com ele vazio).
 */
export type DropTarget = { kind: 'split'; nodeId: string; side: DropSide } | { kind: 'edge'; side: DropSide; win?: string } | { kind: 'center' };

/**
 * Onde uma aba arrastada (ou movida pelo menu) cai: dentro de um espaço, na posição, ou num espaço
 * novo. Um espaço inteiro arrastado para dentro de outro (`into`) junta as abas nele.
 */
export type SheetTarget = { kind: 'into'; nodeId: string; index?: number } | DropTarget;

/** Os tipos de aba que se juntam no mesmo espaço: as conversas, as cenas, as fichas e as tabelas. */
export type TabKind = 'chats' | 'scenes' | 'sheets' | 'tables';

/** Largura (ou altura) inicial de uma coluna (ou faixa) criada na borda da mesa. */
export const NEW_SITE_SIZE = { horizontal: 300, vertical: 220 } as const;
/** Largura inicial da coluna da biblioteca e da dos participantes. */
const LIBRARY_WIDTH = 332;
const USERS_WIDTH = 370;

/** A ordem em que os lugares são percorridos: o centro primeiro, depois as laterais. */
const PLACES: readonly DockPlace[] = ['client', 'left', 'mostLeft', 'right', 'mostRight', 'top', 'bottom'];
const OUTER = { left: 'mostLeft', right: 'mostRight' } as const;

let seq = 0;
export const newLayoutId = (prefix: string) => `${prefix}${Date.now().toString(36)}${(seq++).toString(36)}`;

const content = (...sheets: Sheet[]): ContentNode => ({ kind: 'content', id: newLayoutId('n'), sheets, active: sheets[0]?.id ?? '' });
const sheet = (classId: ModuleClassId, main = false, param?: string): Sheet => ({
  id: newLayoutId('s'),
  classId,
  ...(main ? { main } : {}),
  ...(param !== undefined ? { param } : {}),
});

/** O tipo da aba, para juntar as do mesmo tipo no mesmo espaço; null nas que não se juntam (a biblioteca, a música…). */
export function tabKind(classId: ModuleClassId): TabKind | null {
  switch (classId) {
    case 'system/scene':
      return 'scenes';
    case 'system/mainChat':
    case 'system/pvtChat':
    case 'system/groupPVTChat':
      return 'chats';
    case 'system/charSheet':
      return 'sheets';
    case 'system/tableDock':
      return 'tables';
    default:
      return null;
  }
}

/**
 * Layout inicial: biblioteca à esquerda, chat principal ao centro, participantes à direita, e um
 * espaço vazio junto do chat, que recebe a primeira cena ou ficha. `wide`: a janela é larga (veja
 * `nextToChats`).
 */
export function defaultLayout(wide = true): DesktopLayout {
  const base: DesktopLayout = {
    version: 3,
    sites: {
      left: { node: content(sheet('system/roomLibrary')), size: LIBRARY_WIDTH },
      client: { node: content(sheet('system/mainChat', true)), size: 0 },
      right: { node: content(sheet('system/stdVertUserList')), size: USERS_WIDTH },
    },
  };
  return nextToChats(base, content(), wide);
}

/* ------------------------------------------------------------------ persistência */

/** Onde o layout da mesa fica guardado (a janela de um espaço lê dali as abas dele). */
export const layoutKey = (roomId: string) => `rpgdesk.roomLayout.${roomId}`;

export function loadLayout(roomId: string, wide = true): DesktopLayout {
  try {
    const raw = window.localStorage.getItem(layoutKey(roomId));
    if (raw) {
      const parsed = JSON.parse(raw) as { version?: number; sites?: DesktopLayout['sites'] };
      if ((parsed?.version === 1 || parsed?.version === 2 || parsed?.version === 3) && parsed.sites) {
        const layout = { version: parsed.version, sites: parsed.sites } as unknown as DesktopLayout;
        if (listSheets(layout).some((s) => s.main)) return normalizeLayout(layout);
      }
    }
  } catch {
    // layout corrompido ou armazenamento indisponível: volta ao padrão
  }
  return defaultLayout(wide);
}

export function saveLayout(roomId: string, layout: DesktopLayout): void {
  try {
    window.localStorage.setItem(layoutKey(roomId), JSON.stringify(layout));
  } catch {
    // sem armazenamento: o layout vale só nesta sessão
  }
}

/**
 * Os layouts guardados nas versões 1 e 2 viram a 3. Os painéis da versão 1, com módulos
 * misturados, já valem como estão; saem só os avisos dos painéis Cenas e Fichas vazios (uma aba da
 * cena, ou da ficha, sem item), e o espaço que só tinha o aviso fica vazio, no mesmo lugar. Em
 * qualquer versão, sai o nome que os painéis tinham até 2026-10-29, e o espaço vazio deixa de estar
 * recolhido. A marca de janela própria de antes também muda: a do espaço (`out: true`) passa a ser o
 * id dele, que era o da janela, e a da aba sai (a aba fica na mesa). A aba de um módulo que esta
 * versão não conhece (de uma versão mais nova) também sai. Um layout da versão 3 sem nada disso
 * volta igual (o mesmo objeto).
 */
export function normalizeLayout(layout: DesktopLayout): DesktopLayout {
  const legacy = (layout.version as number) !== 3;
  const hint = (s: Sheet) => legacy && s.param === undefined && (s.classId === 'system/scene' || s.classId === 'system/charSheet');
  const foreign = (s: Sheet) => !KNOWN_MODULES.has(s.classId);
  const oldMark = (x: object) => 'out' in x && typeof (x as { out?: unknown }).out !== 'string';
  const stale = (n: DockNode): boolean =>
    n.kind === 'group'
      ? n.children.some(stale)
      : 'name' in n || oldMark(n) || (!n.sheets.length && !!n.collapsed) || n.sheets.some((s) => hint(s) || foreign(s) || 'out' in s);
  if (!legacy && !Object.values(layout.sites).some((site) => site && stale(site.node))) return layout;
  const clean = (n: DockNode): DockNode => {
    if (n.kind !== 'content') return n;
    const { name: _name, out, ...rest } = n as ContentNode & { name?: string; out?: unknown };
    const sheets = rest.sheets.filter((s) => !hint(s) && !foreign(s)).map(({ out: _out, ...s }: Sheet & { out?: unknown }) => s);
    const mark = typeof out === 'string' ? { out } : out ? { out: n.id } : {};
    const node: ContentNode = { ...rest, ...mark, sheets, active: sheets.some((s) => s.id === rest.active) ? rest.active : (sheets[0]?.id ?? '') };
    return sheets.length ? node : expanded(node);
  };
  return { version: 3, sites: mapSitesRaw(layout, clean).sites };
}

/* ------------------------------------------------------------------ operações (imutáveis) */

/** A marca de janela própria do nó (vazia, se ele não é a raiz de uma). */
const markOf = (n: DockNode): WindowMark => ({ ...(n.out ? { out: n.out } : {}), ...(n.home ? { home: n.home } : {}) });

/** O nó sem a marca de janela própria. */
function unmarked<T extends DockNode>(n: T): T {
  if (!n.out && !n.home) return n;
  const { out: _out, home: _home, ...rest } = n;
  return rest as T;
}

function mapNodes(node: DockNode, fn: (n: DockNode) => DockNode | null): DockNode | null {
  const mapped = fn(node);
  if (!mapped) return null;
  if (mapped.kind === 'group') {
    const children: DockNode[] = [];
    const sizes: number[] = [];
    mapped.children.forEach((c, i) => {
      const m = mapNodes(c, fn);
      if (m) {
        children.push(m);
        sizes.push(mapped.sizes[i] ?? 1);
      }
    });
    if (children.length === 0) return null;
    // a raiz de uma janela que ficou com um filho só passa a marca para ele; se ele já é a raiz de
    // outra janela, a de fora some (tudo o que ela tinha foi para a de dentro)
    if (children.length === 1) return mapped.out && !children[0]!.out ? { ...children[0]!, ...markOf(mapped) } : children[0]!;
    return { ...mapped, children, sizes };
  }
  return mapped;
}

/** Aplica `fn` em todos os nós; nós que viram null somem, lugares sem nó também, e grupos na mesma direção viram um. */
function mapSitesRaw(layout: DesktopLayout, fn: (n: DockNode) => DockNode | null): DesktopLayout {
  const sites: DesktopLayout['sites'] = {};
  for (const [place, site] of Object.entries(layout.sites) as [DockPlace, DockSite][]) {
    const node = mapNodes(site.node, fn);
    if (node) sites[place] = { ...site, node: flatten(node) };
  }
  return { ...layout, sites };
}

/**
 * O chat principal nunca some: sem ele (layout estragado), volta num espaço de conversas da mesa (ele
 * não entra numa janela própria), ou no centro.
 */
function keepMain(layout: DesktopLayout): DesktopLayout {
  if (listSheets(layout).some((s) => s.main)) return layout;
  const main = sheet('system/mainChat', true);
  const chats = deskPanels(layout, null).find((n) => n.sheets.some((s) => tabKind(s.classId) === 'chats'));
  if (chats) return mapSitesRaw(layout, (n) => (n.id === chats.id && n.kind === 'content' ? { ...n, sheets: [main, ...n.sheets], active: main.id } : n));
  return appendTo(layout, 'client', content(main), 'horizontal');
}

function mapSites(layout: DesktopLayout, fn: (n: DockNode) => DockNode | null): DesktopLayout {
  return keepMain(mapSitesRaw(layout, fn));
}

/** Sem a marca de recolhido. */
function expanded(n: ContentNode): ContentNode {
  if (!n.collapsed) return n;
  const { collapsed: _collapsed, ...rest } = n;
  return rest;
}

/** Sem a aba; a que estava à vista passa para a vizinha da esquerda (sem nenhuma, o espaço fica vazio). */
function withoutSheet(n: ContentNode, sheetId: string): ContentNode {
  const index = n.sheets.findIndex((s) => s.id === sheetId);
  const sheets = n.sheets.filter((s) => s.id !== sheetId);
  const active = n.active !== sheetId ? n.active : (sheets[Math.max(0, index - 1)]?.id ?? '');
  return { ...n, sheets, active };
}

/**
 * Põe a aba no espaço, na posição (sem ela, no fim). À vista (`activate`), o espaço recolhido
 * aparece; o espaço vazio a mostra de qualquer jeito, porque é a única.
 */
function addSheet(n: ContentNode, fresh: Sheet, activate: boolean, index?: number): ContentNode {
  const sheets = [...n.sheets];
  sheets.splice(Math.max(0, Math.min(index ?? sheets.length, sheets.length)), 0, fresh);
  if (activate) return { ...expanded(n), sheets, active: fresh.id };
  return { ...n, sheets, active: n.sheets.length ? n.active : fresh.id };
}

/** Põe a aba à vista no espaço dela (e o espaço recolhido volta a aparecer). */
export function activateSheet(layout: DesktopLayout, nodeId: string, sheetId: string): DesktopLayout {
  return mapSites(layout, (n) => (n.kind === 'content' && n.id === nodeId ? { ...expanded(n), active: sheetId } : n));
}

/** Recolhe o espaço (fica só a faixa de abas) ou o mostra de novo. */
export function setCollapsed(layout: DesktopLayout, nodeId: string, collapsed: boolean): DesktopLayout {
  return mapSites(layout, (n) => (n.kind === 'content' && n.id === nodeId ? (collapsed ? { ...n, collapsed: true } : expanded(n)) : n));
}

/**
 * Fecha uma aba (a principal não fecha). A aba à vista passa para a vizinha. A última deixa o
 * espaço vazio, aberto, no mesmo lugar, à espera da próxima (numa janela própria também: a janela
 * continua aberta).
 */
export function closeSheet(layout: DesktopLayout, sheetId: string): DesktopLayout {
  return mapSites(layout, (n) => {
    if (n.kind !== 'content') return n;
    const target = n.sheets.find((s) => s.id === sheetId);
    if (!target || target.main) return n;
    const rest = withoutSheet(n, sheetId);
    // recolhido, o espaço vazio ficaria uma faixa sem aba nenhuma para abri-lo
    return rest.sheets.length ? rest : expanded(rest);
  });
}

/**
 * Fecha o espaço com todas as abas dele ("Fechar o espaço", no ⋯, e o × do espaço vazio). O espaço
 * do chat principal não fecha. O último espaço de uma janela própria fecha a janela junto.
 */
export function closePanel(layout: DesktopLayout, nodeId: string): DesktopLayout {
  const node = findContent(layout, nodeId);
  if (!node || node.sheets.some((s) => s.main)) return layout;
  return mapSites(layout, (n) => (n.id === nodeId ? null : n));
}

/** Move `delta` (em unidades de peso) do filho `index+1` para o filho `index` de um grupo. */
export function setGroupSizes(layout: DesktopLayout, groupId: string, index: number, delta: number): DesktopLayout {
  return mapSites(layout, (n) => {
    if (n.kind !== 'group' || n.id !== groupId) return n;
    const sizes = [...n.sizes];
    const a = sizes[index] ?? 1;
    const b = sizes[index + 1] ?? 1;
    const min = (a + b) * 0.12;
    const na = Math.max(min, Math.min(a + b - min, a + delta));
    sizes[index] = na;
    sizes[index + 1] = a + b - na;
    return { ...n, sizes };
  });
}

/**
 * O tamanho a gravar depois de arrastar o divisor de uma lateral `delta` px na tela. Com as laterais
 * encolhidas para o centro caber, a tela mostra o gravado vezes `factor`: o arraste vale dividido por
 * ele. Somado ao tamanho mostrado, alargar gravaria o encolhido, menor que o de antes.
 */
export function dragSiteSize(stored: number, delta: number, factor = 1): number {
  return stored + delta / (factor > 0 ? factor : 1);
}

/** Uma coluna (ou faixa) da borda nunca fica mais estreita que isso. */
export const SITE_MIN = 160;
/**
 * A faixa de cima ou de baixo vai até 720 px (o centro precisa de altura). A largura de uma coluna
 * não tem esse teto: quem a limita é a mesa, pela largura da janela, para o centro caber
 * (`room/Desk.tsx`); aqui fica só um teto de segurança.
 */
const BAND_MAX = 720;
const COLUMN_MAX = 4000;

export function resizeSite(layout: DesktopLayout, place: DockPlace, size: number): DesktopLayout {
  const site = layout.sites[place];
  if (!site) return layout;
  const max = place === 'top' || place === 'bottom' ? BAND_MAX : COLUMN_MAX;
  return { ...layout, sites: { ...layout.sites, [place]: { ...site, size: Math.round(Math.max(SITE_MIN, Math.min(max, size))) } } };
}

export function findSheet(layout: DesktopLayout, classId: ModuleClassId, param?: string): { nodeId: string; sheetId: string } | null {
  for (const place of PLACES) {
    const site = layout.sites[place];
    const found = site && findIn(site.node, (x) => x.classId === classId && (param === undefined || x.param === param));
    if (found) return found;
  }
  return null;
}

function findIn(n: DockNode, match: (s: Sheet) => boolean): { nodeId: string; sheetId: string } | null {
  if (n.kind === 'content') {
    const s = n.sheets.find(match);
    return s ? { nodeId: n.id, sheetId: s.id } : null;
  }
  for (const c of n.children) {
    const f = findIn(c, match);
    if (f) return f;
  }
  return null;
}

/** Os espaços do layout, na ordem dos lugares (o centro primeiro). */
function panels(layout: DesktopLayout): ContentNode[] {
  const out: ContentNode[] = [];
  const walk = (n: DockNode) => {
    if (n.kind === 'content') out.push(n);
    else n.children.forEach(walk);
  };
  for (const place of PLACES) {
    const s = layout.sites[place];
    if (s) walk(s.node);
  }
  return out;
}

export const listPanels = panels;

/**
 * Os nós do layout, na ordem dos lugares, cada um com a janela própria em que está (null: na mesa).
 * A raiz de uma janela conta como dentro dela.
 */
function nodesOf(layout: DesktopLayout): { node: DockNode; win: string | null }[] {
  const list: { node: DockNode; win: string | null }[] = [];
  const walk = (node: DockNode, win: string | null) => {
    const here = node.out ?? win;
    list.push({ node, win: here });
    if (node.kind === 'group') node.children.forEach((c) => walk(c, here));
  };
  for (const place of PLACES) {
    const site = layout.sites[place];
    if (site) walk(site.node, null);
  }
  return list;
}

/** Os espaços que aparecem na mesa (`win` null) ou numa janela própria (sem os das janelas de dentro dela). */
function deskPanels(layout: DesktopLayout, win: string | null): ContentNode[] {
  return nodesOf(layout).flatMap((x) => (x.node.kind === 'content' && x.win === win ? [x.node] : []));
}

/** O espaço do chat principal. */
export function chatPanel(layout: DesktopLayout): ContentNode | null {
  return panels(layout).find((n) => n.sheets.some((s) => s.main)) ?? null;
}

/** A aba de um módulo, como `findSheet`; o chat é o principal, e não uma cópia dele. */
export function findTab(layout: DesktopLayout, classId: ModuleClassId, param?: string): { nodeId: string; sheetId: string } | null {
  if (classId !== 'system/mainChat') return findSheet(layout, classId, param);
  const panel = chatPanel(layout);
  const main = panel?.sheets.find((s) => s.main);
  return panel && main ? { nodeId: panel.id, sheetId: main.id } : null;
}

export function findContent(layout: DesktopLayout, nodeId: string): ContentNode | null {
  return panels(layout).find((n) => n.id === nodeId) ?? null;
}

/** O espaço que tem a aba. */
export function sheetPanel(layout: DesktopLayout, sheetId: string): ContentNode | null {
  return panels(layout).find((n) => n.sheets.some((s) => s.id === sheetId)) ?? null;
}

/** O lugar de doca que tem o nó. */
function placeOf(layout: DesktopLayout, nodeId: string): DockPlace | null {
  const has = (n: DockNode): boolean => n.id === nodeId || (n.kind === 'group' && n.children.some(has));
  return PLACES.find((place) => {
    const site = layout.sites[place];
    return !!site && has(site.node);
  }) ?? null;
}

/**
 * Como uma aba nova abre (`openTab`):
 * - `activate`: a pessoa pediu, e a aba fica à vista; sem ele (a conversa que chegou, a tabela que o
 *   mestre ativou para todos), a que estava continua;
 * - `detached`: a aba vai logo em seguida para uma janela própria (abrir em nova janela pela
 *   biblioteca);
 * - `recent`: os espaços em que a pessoa clicou, na mesa e nas janelas, do mais recente para o mais
 *   antigo;
 * - `into`: o espaço que recebe (o "+" da faixa de abas);
 * - `within`: a janela própria de onde veio o pedido (a biblioteca, ou os participantes, que estão
 *   nela).
 */
/** `avoid`: um espaço que a aba nova não cobre se houver outro (o do chat, quando o relógio abre a aba Sessões). */
export type OpenOpts = { activate?: boolean; detached?: boolean; recent?: readonly string[]; into?: string; within?: string; avoid?: string };

/** As abas de onde se abre o resto: o último espaço usado não as cobre. */
const SOURCE_TABS: ReadonlySet<ModuleClassId> = new Set<ModuleClassId>(['system/roomLibrary', 'system/stdVertUserList']);

/**
 * O espaço que recebe uma aba nova (docs/spec/room-layout.md, "Para onde vai uma aba nova"):
 * 1. junto das do mesmo tipo (a cena com as cenas, a conversa com os chats): pedida pela mesa, em
 *    qualquer janela; pedida de dentro de uma janela própria (`within`), só nela. As conversas vão
 *    para junto de outras conversas (ou de uma cópia do chat); sem nenhuma, para o espaço do chat;
 * 2. a primeira do tipo, num espaço vazio de onde veio o pedido (a mesa, ou a janela), se a pessoa
 *    pediu a aba e ela fica ali (a tabela que chegou sozinha e o item aberto direto numa janela não
 *    ocupam o espaço vazio);
 * 3. senão, no último espaço usado ali, menos os que mostram a Biblioteca ou os Participantes, de
 *    onde se abre o resto (sem nenhum outro, vale um deles); com `avoid`, aquele espaço só recebe a
 *    aba se não houver nenhum outro;
 * 4. null: não há espaço na mesa, e a aba ganha um espaço novo.
 * Entre os espaços que servem, vale o usado por último; depois, os da mesa antes dos das janelas, e
 * a ordem dos lugares (o centro primeiro).
 */
export function tabDestination(layout: DesktopLayout, classId: ModuleClassId, opts: OpenOpts = {}): string | null {
  const all = nodesOf(layout).flatMap((x) => (x.node.kind === 'content' ? [{ node: x.node, win: x.win }] : []));
  if (opts.into && all.some((x) => x.node.id === opts.into)) return opts.into;
  const recent = opts.recent ?? [];
  const deskOf = new Map(all.map((x) => [x.node.id, x.win]));
  const rank = (n: ContentNode) => {
    const i = recent.indexOf(n.id);
    return i >= 0 ? i : recent.length + (deskOf.get(n.id) ? 1 : 0);
  };
  // a ordenação é estável: os que a pessoa não usou ficam na ordem dos lugares
  const first = (list: ContentNode[]) => [...list].sort((a, b) => rank(a) - rank(b))[0] ?? null;
  // de onde veio o pedido: a janela própria (se ainda está aberta), ou a mesa
  const from = opts.within && all.some((x) => x.win === opts.within) ? opts.within : null;
  const here = all.filter((x) => x.win === from).map((x) => x.node);
  const kind = tabKind(classId);
  const pool = from ? here : all.map((x) => x.node);
  // o chat da mesa sozinho não chama as conversas quando há outras (o espaço do chat numa janela
  // tem uma cópia dele, e as conversas vão para lá)
  const same = kind
    ? (first(pool.filter((n) => n.sheets.some((s) => !s.main && tabKind(s.classId) === kind))) ??
      (kind === 'chats' ? (pool.find((n) => n.sheets.some((s) => s.main)) ?? null) : null))
    : null;
  if (same) return same.id;
  const empty = opts.activate && !opts.detached ? first(here.filter((n) => n.sheets.length === 0)) : null;
  if (empty) return empty.id;
  const busy = here.filter((n) => n.sheets.length > 0);
  const others = opts.avoid ? busy.filter((n) => n.id !== opts.avoid) : busy;
  const source = (n: ContentNode) => SOURCE_TABS.has(n.sheets.find((s) => s.id === n.active)?.classId ?? 'system/mainChat');
  return (first(others.filter((n) => !source(n))) ?? first(others) ?? first(busy) ?? first(here))?.id ?? null;
}

/**
 * Abre a aba de um módulo (`param`: a cena, a ficha, a conversa, a tabela) no espaço que
 * `tabDestination` escolhe; sem nenhum espaço na mesa, ela nasce num espaço novo, no centro. O que
 * já está na mesa só fica à vista (com `into`, vem para aquele espaço; o chat da mesa não sai da
 * janela principal, e num espaço de uma janela própria fica uma cópia dele).
 */
export function openTab(layout: DesktopLayout, classId: ModuleClassId, param?: string, opts: OpenOpts = {}): DesktopLayout {
  const activate = opts.activate ?? true;
  const existing = findTab(layout, classId, param);
  if (existing) {
    if (opts.into && existing.nodeId !== opts.into && findContent(layout, opts.into)) {
      if (classId === 'system/mainChat' && windowOfNode(layout, opts.into)) return chatCopyIn(layout, opts.into);
      return moveSheet(layout, existing.sheetId, { kind: 'into', nodeId: opts.into });
    }
    return activate ? activateSheet(layout, existing.nodeId, existing.sheetId) : layout;
  }
  const fresh = sheet(classId, false, param);
  const dest = tabDestination(layout, classId, { ...opts, activate });
  if (!dest) return keepMain(appendTo(layout, 'client', content(fresh), 'horizontal'));
  return mapSites(layout, (n) => (n.kind === 'content' && n.id === dest ? addSheet(n, fresh, activate) : n));
}

/**
 * Põe o espaço que nasce junto do chat principal, maior que ele: à esquerda dele numa janela larga
 * (a cena ao lado do chat, como na mesa de referência), em cima dele numa estreita, onde lado a
 * lado os dois ficariam apertados. Com o chat fora do centro, o espaço vai para o centro.
 */
function nextToChats(layout: DesktopLayout, fresh: DockNode, wide: boolean): DesktopLayout {
  const chats = chatPanel(layout);
  const client = layout.sites.client;
  if (!chats || !client || placeOf(layout, chats.id) !== 'client') return appendTo(layout, 'client', fresh, 'horizontal', 'start');
  const node = insertBefore(client.node, chats.id, wide ? 'horizontal' : 'vertical', fresh, wide ? 2 : 1.5);
  return { ...layout, sites: { ...layout.sites, client: { ...client, node: flatten(node) } } };
}

/** Põe `fresh` antes do nó `targetId`, na direção dada, com `factor` vezes o peso dele. */
function insertBefore(node: DockNode, targetId: string, orientation: 'horizontal' | 'vertical', fresh: DockNode, factor: number): DockNode {
  if (node.id === targetId) return { kind: 'group', id: newLayoutId('g'), orientation, children: [fresh, node], sizes: [factor, 1] };
  if (node.kind !== 'group') return node;
  const index = node.children.findIndex((c) => c.id === targetId);
  if (index >= 0 && node.orientation === orientation) {
    const children = [...node.children];
    const sizes = [...node.sizes];
    children.splice(index, 0, fresh);
    sizes.splice(index, 0, (sizes[index] ?? 1) * factor);
    return { ...node, children, sizes };
  }
  return { ...node, children: node.children.map((c) => insertBefore(c, targetId, orientation, fresh, factor)) };
}

/**
 * Põe um espaço no começo ou no fim de um lugar, na direção dada: empilhado numa coluna, lado a
 * lado no centro. O lugar vazio nasce com `size`. O lugar que é a raiz de uma janela própria não
 * recebe o espaço dentro dela: os dois ficam lado a lado.
 */
function appendTo(layout: DesktopLayout, place: DockPlace, fresh: DockNode, orientation: 'horizontal' | 'vertical', at: 'start' | 'end' = 'end', size?: number): DesktopLayout {
  const site = layout.sites[place];
  if (!site) {
    const initial = size ?? (place === 'client' ? 0 : place === 'top' || place === 'bottom' ? NEW_SITE_SIZE.vertical : NEW_SITE_SIZE.horizontal);
    return { ...layout, sites: { ...layout.sites, [place]: { node: fresh, size: initial } } };
  }
  const same = site.node.kind === 'group' && site.node.orientation === orientation && !site.node.out;
  const children = same && site.node.kind === 'group' ? site.node.children : [site.node];
  const sizes = same && site.node.kind === 'group' ? site.node.sizes : [1];
  // o novo fica com 2/3 da média: embaixo de um espaço sozinho, divide 3:2
  const weight = (sizes.reduce((a, b) => a + b, 0) / sizes.length) * (2 / 3);
  const node: DockNode = {
    kind: 'group',
    id: same ? site.node.id : newLayoutId('g'),
    orientation,
    children: at === 'start' ? [fresh, ...children] : [...children, fresh],
    sizes: at === 'start' ? [weight, ...sizes] : [...sizes, weight],
  };
  return { ...layout, sites: { ...layout.sites, [place]: { ...site, node: flatten(node) } } };
}

/**
 * Divide o espaço `nodeId`, com o novo do lado dado. `keep`: o novo não tira largura do espaço (é a
 * janela própria escondida ao lado dele: na mesa, nada muda de tamanho).
 */
function splitAt(layout: DesktopLayout, nodeId: string, side: DropSide, fresh: DockNode, keep = false): DesktopLayout {
  const sites: DesktopLayout['sites'] = {};
  for (const [place, site] of Object.entries(layout.sites) as [DockPlace, DockSite][]) {
    sites[place] = { ...site, node: flatten(splitNode(site.node, nodeId, side, fresh, keep)) };
  }
  return { ...layout, sites };
}

/** A aba está à vista: é a ativa do espaço dela, e o espaço não está recolhido (no layout desenhado, `visibleLayout`). */
export function isActiveSheet(layout: DesktopLayout, sheetId: string): boolean {
  const panel = panels(layout).find((n) => n.sheets.some((s) => s.id === sheetId));
  return !!panel && panel.active === sheetId && !panel.collapsed;
}

/** Um lado da mesa, no "Mover para" do menu do espaço. */
export type PanelSide = 'left' | 'center' | 'right';

/** De que lado está o espaço: as colunas de cada lado contam como o lado; o resto é o centro. */
export function sideOf(layout: DesktopLayout, nodeId: string): PanelSide | null {
  const place = placeOf(layout, nodeId);
  if (!place) return null;
  if (place === 'left' || place === 'mostLeft') return 'left';
  if (place === 'right' || place === 'mostRight') return 'right';
  return 'center';
}

/**
 * Leva o espaço para um lado da mesa: embaixo da coluna daquele lado (a de dentro, ou a de fora,
 * se só ela existe; sem nenhuma, nasce uma) ou, no centro, ao lado do que está lá (ou ocupando o
 * centro vazio). O espaço de uma janela própria sai dela ("Mover para a mesa").
 */
export function movePanelTo(layout: DesktopLayout, nodeId: string, side: PanelSide): DesktopLayout {
  const node = findContent(layout, nodeId);
  if (!node || (!windowOfNode(layout, nodeId) && sideOf(layout, nodeId) === side)) return layout;
  const rest = removeNode(layout, nodeId);
  const moving = unmarked(expanded(node));
  if (side === 'center') return appendTo(rest, 'client', moving, 'horizontal');
  const place: DockPlace | null = rest.sites[side] ? side : rest.sites[OUTER[side]] ? OUTER[side] : null;
  return place ? appendTo(rest, place, moving, 'vertical') : addAtEdge(rest, side, moving);
}

/** Fecha todas as abas de um módulo, na mesa e nas janelas próprias (ex.: a mesa desligou as Sessões). */
export function withoutModule(layout: DesktopLayout, classId: ModuleClassId): DesktopLayout {
  return listSheets(layout)
    .filter((s) => s.classId === classId)
    .reduce((acc, s) => closeSheet(acc, s.id), layout);
}

/** Fecha todas as abas de um módulo com esse parâmetro (ex.: saí do grupo). */
export function closeParamSheets(layout: DesktopLayout, classId: ModuleClassId, param: string): DesktopLayout {
  let next = layout;
  for (let found = findSheet(next, classId, param); found; found = findSheet(next, classId, param)) next = closeSheet(next, found.sheetId);
  return next;
}

/* ------------------------------------------------------------------ janelas próprias */

/*
 * Uma janela própria (docs/spec/popout-windows.md) é um pedaço da árvore da mesa, com a marca dela
 * na raiz (`out`). A mesa desenha sem ele (`visibleLayout`), a janela desenha só ele (`windowView`),
 * e dentro dela valem as operações da mesa: dividir a raiz (`splitNode`) faz do grupo novo a raiz, e
 * o grupo-raiz que fica com um filho só passa a marca para ele (`mapNodes`). Fechar a janela tira a
 * marca, e tudo volta no lugar (`attachWindow`). O chat da mesa nunca entra numa janela: vai uma
 * cópia dele.
 */

/** Módulo que abre um item da biblioteca: a cena, ou a ficha (de plugin, tabela). */
export const itemModule = (kind: string | undefined): ModuleClassId => (kind === 'scene' ? 'system/scene' : 'system/charSheet');

/** Os ids dos espaços do layout. */
export const layoutNodeIds = (layout: DesktopLayout): string[] => panels(layout).map((n) => n.id);

/** Todas as abas do layout, na ordem: centro primeiro, depois as laterais. */
export function listSheets(layout: DesktopLayout): Sheet[] {
  return panels(layout).flatMap((n) => n.sheets);
}

/** A raiz da janela própria `win` (o nó com a marca dela). */
export function windowRoot(layout: DesktopLayout, win: string): DockNode | null {
  return nodesOf(layout).find((x) => x.node.out === win)?.node ?? null;
}

/** A janela própria em que o espaço (ou o grupo) aparece; null na mesa. */
export function windowOfNode(layout: DesktopLayout, nodeId: string): string | null {
  return nodesOf(layout).find((x) => x.node.id === nodeId)?.win ?? null;
}

/** A janela própria que mostra a aba; null se ela está na mesa (o chat da mesa está sempre). */
export function windowOf(layout: DesktopLayout, sheetId: string): string | null {
  const node = sheetPanel(layout, sheetId);
  return node ? windowOfNode(layout, node.id) : null;
}

/** Os ids das janelas próprias da mesa (as de dentro de outra também). */
export function windowIds(layout: DesktopLayout): string[] {
  return nodesOf(layout).flatMap((x) => (x.node.out ? [x.node.out] : []));
}

/** Os espaços de todas as janelas, cada um com a janela em que aparece (null: na mesa), os da mesa primeiro. */
export function spacesByDesk(layout: DesktopLayout): { node: ContentNode; win: string | null }[] {
  const all = nodesOf(layout).flatMap((x) => (x.node.kind === 'content' ? [{ node: x.node, win: x.win }] : []));
  return [...all.filter((x) => !x.win), ...all.filter((x) => x.win)];
}

/** O layout que a mesa desenha: sem as janelas próprias. Não grava nada (o layout guardado não muda). */
export function visibleLayout(layout: DesktopLayout): DesktopLayout {
  if (!windowIds(layout).length) return layout;
  const sites: DesktopLayout['sites'] = {};
  for (const [place, site] of Object.entries(layout.sites) as [DockPlace, DockSite][]) {
    const node = mapNodes(site.node, (n) => (n.out ? null : n));
    // o lugar que está todo numa janela some da mesa (o centro fica vazio)
    if (node) sites[place] = { ...site, node };
  }
  return { ...layout, sites };
}

/** O que a janela própria `win` desenha: o pedaço dela, sem as janelas de dentro dela. */
export function windowView(layout: DesktopLayout, win: string): DockNode | null {
  const root = windowRoot(layout, win);
  return root ? mapNodes(unmarked(root), (n) => (n.out ? null : n)) : null;
}

/**
 * Onde nasce o espaço novo ao lado de um ("Espaço novo ao lado", a janela de uma aba): à direita no
 * centro e nas janelas próprias, embaixo numa coluna.
 */
export function besideSide(layout: DesktopLayout, nodeId: string): DropSide {
  return windowOfNode(layout, nodeId) || sideOf(layout, nodeId) === 'center' ? 'right' : 'bottom';
}

/**
 * Uma janela própria nova, vazia ("Nova janela", no menu Abas): um espaço vazio com a marca dela, ao
 * lado do chat, onde o que ela tiver aparece quando ela fechar. `wide`: veja `nextToChats`.
 */
export function newWindow(layout: DesktopLayout, win: string, wide = true): DesktopLayout {
  if (windowRoot(layout, win)) return layout;
  return nextToChats(layout, { ...content(), out: win }, wide);
}

/**
 * Leva a aba para uma janela própria nova: um espaço com ela, escondido ao lado do espaço de onde ela
 * saiu, que guarda de onde ela veio (`home`). O espaço que fica sem abas sai, e a janela fica no
 * lugar dele. Do chat da mesa vai uma cópia: ele não sai da janela principal.
 */
export function popTab(layout: DesktopLayout, sheetId: string, win: string): DesktopLayout {
  const from = sheetPanel(layout, sheetId);
  const tab = from?.sheets.find((s) => s.id === sheetId);
  if (!from || !tab || windowRoot(layout, win)) return layout;
  const fresh: ContentNode = { ...content(tab.main ? sheet('system/mainChat') : tab), out: win, home: { nodeId: from.id, index: from.sheets.indexOf(tab) } };
  const placed = splitAt(layout, from.id, besideSide(layout, from.id), fresh, true);
  if (tab.main) return placed;
  return mapSites(placed, (n) => {
    if (n.kind !== 'content' || n.id !== from.id) return n;
    const rest = withoutSheet(n, sheetId);
    return rest.sheets.length ? rest : null;
  });
}

/**
 * Leva o espaço inteiro, com as abas, para uma janela própria: ele ganha a marca dela e fica no
 * lugar, escondido. O espaço do chat da mesa fica, só com o chat (que não sai da janela principal):
 * a janela recebe um espaço com uma cópia dele e as outras abas, que volta para o do chat.
 */
export function popPanel(layout: DesktopLayout, nodeId: string, win: string): DesktopLayout {
  const node = findContent(layout, nodeId);
  if (!node || !node.sheets.length || node.out || windowRoot(layout, win)) return layout;
  const main = node.sheets.find((s) => s.main);
  if (!main) return mapSites(layout, (n) => (n.id === nodeId ? { ...n, out: win } : n));
  const copy = sheet('system/mainChat');
  const fresh: ContentNode = {
    kind: 'content',
    id: newLayoutId('n'),
    sheets: node.sheets.map((s) => (s.main ? copy : s)),
    active: node.active === main.id ? copy.id : node.active,
    out: win,
    home: { nodeId, index: 0 },
  };
  const placed = splitAt(layout, nodeId, besideSide(layout, nodeId), fresh, true);
  return mapSites(placed, (n) => (n.kind === 'content' && n.id === nodeId ? { ...expanded(n), sheets: [main], active: main.id } : n));
}

/** Uma cópia do chat da mesa (numa janela própria, ou duplicada): ela não volta com a janela. */
const isChatCopy = (s: Sheet) => s.classId === 'system/mainChat' && !s.main;

/**
 * As abas de uma janela que nasceu de uma aba voltam para o espaço de onde ela saiu, na posição, e a
 * que estava à vista na janela fica à vista. As cópias do chat não voltam; no espaço do chat, as abas
 * voltam em volta do chat da mesa, na ordem em que estavam em volta da cópia.
 */
function mergeHome(into: ContentNode, back: ContentNode, index: number): ContentNode {
  const main = into.sheets.find((s) => s.main);
  const incoming = back.sheets.filter((s) => !isChatCopy(s));
  if (!incoming.length) return into;
  const copyAt = main ? back.sheets.findIndex(isChatCopy) : -1;
  let sheets: Sheet[];
  if (main && copyAt >= 0) {
    const before = back.sheets.slice(0, copyAt).filter((s) => !isChatCopy(s));
    const after = back.sheets.slice(copyAt + 1).filter((s) => !isChatCopy(s));
    sheets = into.sheets.flatMap((s) => (s.main ? [...before, s, ...after] : [s]));
  } else {
    sheets = [...into.sheets];
    sheets.splice(Math.max(0, Math.min(index, sheets.length)), 0, ...incoming);
  }
  const active = incoming.some((s) => s.id === back.active) ? back.active : main && back.sheets[copyAt]?.id === back.active ? main.id : incoming[0]!.id;
  return { ...expanded(into), sheets, active };
}

/**
 * A janela própria `win` fechou: o pedaço dela volta para a mesa, no lugar em que está, aberto, sem
 * as cópias do chat (o chat da mesa já está lá) e sem os espaços vazios. A janela que nasceu de uma
 * aba, com um espaço só, volta para o espaço de onde a aba saiu (`mergeHome`), se ele ainda existe.
 * As janelas de dentro dela continuam abertas.
 */
export function attachWindow(layout: DesktopLayout, win: string): DesktopLayout {
  const root = windowRoot(layout, win);
  if (!root) return layout;
  // `copies`: as cópias do chat ficam (para `mergeHome` saber onde estava o chat)
  const prune = (n: DockNode, copies: boolean): DockNode | null => {
    if (n !== root && n.out) return n;
    if (n.kind === 'content') {
      const sheets = copies ? n.sheets : n.sheets.filter((s) => !isChatCopy(s));
      if (!sheets.length) return null;
      return sheets.length === n.sheets.length ? n : { ...n, sheets, active: sheets.some((s) => s.id === n.active) ? n.active : sheets[0]!.id };
    }
    const kept = n.children.flatMap((c, i) => {
      const p = prune(c, copies);
      return p ? [{ node: p, size: n.sizes[i] ?? 1 }] : [];
    });
    if (!kept.length) return null;
    if (kept.length === 1) return n.out && !kept[0]!.node.out ? { ...kept[0]!.node, ...markOf(n) } : kept[0]!.node;
    return { ...n, children: kept.map((k) => k.node), sizes: kept.map((k) => k.size) };
  };
  const whole = prune(root, true);
  const home = whole && whole.out === win && whole.kind === 'content' && whole.home ? findContent(layout, whole.home.nodeId) : null;
  const back = home ? whole : prune(root, false);
  return mapSites(layout, (n) => {
    if (n === root) {
      if (!back || home) return null;
      // sobrou só uma janela de dentro: ela continua aberta, no lugar desta
      if (back.out !== win) return back;
      const plain = unmarked(back);
      return plain.kind === 'content' ? expanded(plain) : plain;
    }
    return home && back?.kind === 'content' && n.kind === 'content' && n.id === home.id ? mergeHome(n, back, back.home!.index) : n;
  });
}

/* ------------------------------------------------------------------ mover espaços e abas */

/** Tira o nó do lugar dele (até o do chat principal, que vai para outro lugar logo em seguida). */
function removeNode(layout: DesktopLayout, nodeId: string): DesktopLayout {
  return mapSitesRaw(layout, (n) => (n.id === nodeId ? null : n));
}

const orientationOf = (side: DropSide): 'horizontal' | 'vertical' => (side === 'left' || side === 'right' ? 'horizontal' : 'vertical');
const goesFirst = (side: DropSide) => side === 'left' || side === 'top';

/** O nó e o novo lado a lado, num grupo novo. */
function beside(node: DockNode, side: DropSide, fresh: DockNode): DockNode {
  return { kind: 'group', id: newLayoutId('g'), orientation: orientationOf(side), children: goesFirst(side) ? [fresh, node] : [node, fresh], sizes: [1, 1] };
}

/**
 * Põe o nó `fresh` ao lado do nó `targetId`: no grupo dele, se ele vai na mesma direção, ou num grupo
 * novo. A raiz de uma janela própria se divide dentro da janela: o grupo novo passa a ser a raiz.
 */
function splitNode(node: DockNode, targetId: string, side: DropSide, fresh: DockNode, keep = false): DockNode {
  const orientation = orientationOf(side);
  if (node.id === targetId) return { ...beside(unmarked(node), side, fresh), ...markOf(node) };
  if (node.kind !== 'group') return node;
  const index = node.children.findIndex((c) => c.id === targetId);
  // ao lado da raiz de uma janela, no grupo de fora, o novo ficaria fora da janela
  if (index >= 0 && node.orientation === orientation && !node.children[index]!.out) {
    // o espaço divide o lugar dele com o novo (com `keep`, o novo ganha um lugar do mesmo tamanho)
    const weight = node.sizes[index] ?? 1;
    const children = [...node.children];
    const sizes = [...node.sizes];
    const at = goesFirst(side) ? index : index + 1;
    if (!keep) sizes[index] = weight / 2;
    children.splice(at, 0, fresh);
    sizes.splice(at, 0, keep ? weight : weight / 2);
    return { ...node, children, sizes };
  }
  return { ...node, children: node.children.map((c) => splitNode(c, targetId, side, fresh, keep)) };
}

/**
 * Grupos dentro de grupos na mesma direção viram um grupo só (o peso de cada um se mantém). A raiz de
 * uma janela própria fica inteira.
 */
function flatten(node: DockNode): DockNode {
  if (node.kind !== 'group') return node;
  const children: DockNode[] = [];
  const sizes: number[] = [];
  node.children.forEach((child, i) => {
    const flat = flatten(child);
    const weight = node.sizes[i] ?? 1;
    if (flat.kind === 'group' && flat.orientation === node.orientation && !flat.out) {
      const total = flat.sizes.reduce((a, b) => a + b, 0) || 1;
      flat.children.forEach((c, j) => {
        children.push(c);
        sizes.push((weight * (flat.sizes[j] ?? 1)) / total);
      });
    } else {
      children.push(flat);
      sizes.push(weight);
    }
  });
  return { ...node, children, sizes };
}

/**
 * Uma coluna (ou faixa) nova na borda da mesa, por fora das que já existem (e por fora da janela
 * própria que estiver no lugar).
 */
function addAtEdge(layout: DesktopLayout, side: DropSide, fresh: DockNode): DesktopLayout {
  const sites = { ...layout.sites };
  const size = side === 'left' || side === 'right' ? NEW_SITE_SIZE.horizontal : NEW_SITE_SIZE.vertical;
  const wrap = (site: DockSite): DockSite => ({ ...site, node: flatten(beside(site.node, side, fresh)) });
  if (side === 'left' || side === 'right') {
    const inner: DockPlace = side;
    const outer: DockPlace = OUTER[side];
    if (!sites[inner] && !sites[outer]) sites[inner] = { node: fresh, size };
    else if (!sites[outer]) sites[outer] = { node: fresh, size };
    else if (!sites[inner]) {
      // só a de fora existe: ela passa para dentro, e a nova fica por fora
      sites[inner] = sites[outer]!;
      sites[outer] = { node: fresh, size };
    } else sites[outer] = wrap(sites[outer]!);
  } else {
    const place: DockPlace = side;
    sites[place] = sites[place] ? wrap(sites[place]!) : { node: fresh, size };
  }
  return { ...layout, sites };
}

/** O destino fica numa janela própria? (o chat da mesa não vai para lá) */
function entersWindow(layout: DesktopLayout, to: SheetTarget): boolean {
  if (to.kind === 'edge') return !!to.win;
  return to.kind !== 'center' && !!windowOfNode(layout, to.nodeId);
}

/** Põe um espaço no destino (a borda de uma janela própria divide a raiz dela). Devolve `null` se o destino não existe mais. */
function insertAt(layout: DesktopLayout, target: DropTarget, fresh: ContentNode): DesktopLayout | null {
  if (target.kind === 'edge') {
    if (!target.win) return addAtEdge(layout, target.side, fresh);
    const root = windowRoot(layout, target.win);
    return root ? splitAt(layout, root.id, target.side, fresh) : null;
  }
  if (target.kind === 'center') return appendTo(layout, 'client', fresh, 'horizontal');
  if (!findContent(layout, target.nodeId)) return null;
  return splitAt(layout, target.nodeId, target.side, fresh);
}

/**
 * Leva um espaço, com todas as abas dele, para outro lugar (arrastar a parte vazia da faixa de
 * abas): a borda de outro espaço, que se divide, a borda da mesa ou o centro vazio; no lugar novo,
 * ele aparece aberto. Para dentro de outro espaço (`into`), as abas se juntam às dele, na posição
 * (sem ela, no fim), na ordem em que estavam; a que estava à vista continua à vista e o espaço que
 * recebe abre se estava recolhido. Soltar o espaço nele mesmo não muda nada, e o espaço vazio não tem
 * abas para juntar. O espaço do chat da mesa não vai para uma janela própria.
 */
export function movePanel(layout: DesktopLayout, nodeId: string, target: SheetTarget): DesktopLayout {
  const node = findContent(layout, nodeId);
  if (!node || (node.sheets.some((s) => s.main) && entersWindow(layout, target))) return layout;
  if (target.kind === 'into') {
    const into = findContent(layout, target.nodeId);
    if (!into || into.id === nodeId || !node.sheets.length) return layout;
    const at = Math.max(0, Math.min(target.index ?? into.sheets.length, into.sheets.length));
    const active = node.sheets.find((s) => s.id === node.active)?.id ?? node.sheets[0]?.id ?? into.active;
    return mapSites(layout, (n) => {
      if (n.id === nodeId) return null;
      if (n.kind !== 'content' || n.id !== into.id) return n;
      const sheets = [...n.sheets];
      sheets.splice(at, 0, ...node.sheets);
      return { ...expanded(n), sheets, active };
    });
  }
  if (target.kind === 'split' && (target.nodeId === nodeId || !findContent(layout, target.nodeId))) return layout;
  return insertAt(removeNode(layout, nodeId), target, unmarked(expanded(node))) ?? layout;
}

/**
 * Leva uma aba para outro espaço (na posição; no mesmo espaço, reordena) ou para um espaço novo no
 * destino: a borda de um espaço, que se divide, a borda da mesa ou o centro vazio. A aba chega à
 * vista. O espaço de onde saiu a última aba sai junto (era só a moldura dela; o último de uma janela
 * própria fecha a janela). Um espaço não se divide com a própria aba, se ela é a única. O chat da mesa
 * não vai para uma janela própria.
 */
export function moveSheet(layout: DesktopLayout, sheetId: string, to: SheetTarget): DesktopLayout {
  const from = sheetPanel(layout, sheetId);
  const moving = from?.sheets.find((s) => s.id === sheetId);
  if (!from || !moving || (moving.main && entersWindow(layout, to))) return layout;
  const leave = (n: ContentNode): ContentNode | null => {
    const rest = withoutSheet(n, sheetId);
    return rest.sheets.length ? rest : null;
  };
  if (to.kind === 'into') {
    const target = findContent(layout, to.nodeId);
    if (!target) return layout;
    if (target.id === from.id) {
      const sheets = from.sheets.filter((s) => s.id !== sheetId);
      sheets.splice(Math.max(0, Math.min(to.index ?? sheets.length, sheets.length)), 0, moving);
      return mapSites(layout, (n) => (n.kind === 'content' && n.id === from.id ? { ...expanded(n), sheets, active: sheetId } : n));
    }
    return mapSites(layout, (n) => {
      if (n.kind !== 'content') return n;
      if (n.id === from.id) return leave(n);
      return n.id === target.id ? addSheet(n, moving, true, to.index) : n;
    });
  }
  if (to.kind === 'split' && (!findContent(layout, to.nodeId) || (to.nodeId === from.id && from.sheets.length < 2))) return layout;
  const rest = mapSitesRaw(layout, (n) => (n.kind === 'content' && n.id === from.id ? leave(n) : n));
  const next = insertAt(rest, to, content(moving));
  return next ? keepMain(next) : layout;
}

/**
 * Os módulos que podem ter uma cópia na mesa ("Arrastar para clonar" do original): vistas a mais
 * do mesmo conteúdo, como um segundo chat ou a mesma cena com outro zoom. As fichas e as tabelas
 * de plugin ficam de fora (rodam o código do plugin).
 */
export const CLONABLE: ReadonlySet<ModuleClassId> = new Set<ModuleClassId>([
  'system/mainChat',
  'system/stdVertUserList',
  'system/roomLibrary',
  'system/scene',
  'system/musicPlayer',
  'system/sfxPlayer',
]);

/**
 * Uma cópia da aba: num espaço novo, no destino, ou dentro de outro espaço. A cópia do chat
 * principal pode ser fechada (só o original não).
 */
export function cloneSheet(layout: DesktopLayout, sheetId: string, target: SheetTarget): DesktopLayout {
  const original = listSheets(layout).find((s) => s.id === sheetId);
  if (!original || !CLONABLE.has(original.classId)) return layout;
  const copy = sheet(original.classId, false, original.param);
  if (target.kind === 'into') {
    if (!findContent(layout, target.nodeId) || sheetPanel(layout, sheetId)?.id === target.nodeId) return layout;
    return mapSites(layout, (n) => (n.kind === 'content' && n.id === target.nodeId ? addSheet(n, copy, true, target.index) : n));
  }
  const next = insertAt(layout, target, content(copy));
  return next ? keepMain(next) : layout;
}

/**
 * Põe um módulo num lugar (o "+" do modo de edição): um espaço novo com a aba dele. A aba que já
 * está na mesa (o chat, a biblioteca, uma tabela) muda de lugar em vez de abrir outra vez; numa janela
 * própria, o chat da mesa fica onde está, e vai uma cópia dele.
 */
export function placeModule(layout: DesktopLayout, classId: ModuleClassId, target: DropTarget, param?: string): DesktopLayout {
  const existing = findTab(layout, classId, param);
  if (existing && !(classId === 'system/mainChat' && entersWindow(layout, target))) return moveSheet(layout, existing.sheetId, target);
  const next = insertAt(layout, target, content(sheet(classId, false, param)));
  return next ? keepMain(next) : layout;
}

/** Uma cópia do chat da mesa no espaço (o "+" numa janela própria); a que já está nele só fica à vista. */
function chatCopyIn(layout: DesktopLayout, nodeId: string): DesktopLayout {
  const copy = findContent(layout, nodeId)?.sheets.find((s) => s.classId === 'system/mainChat');
  if (copy) return activateSheet(layout, nodeId, copy.id);
  return mapSites(layout, (n) => (n.kind === 'content' && n.id === nodeId ? addSheet(n, sheet('system/mainChat'), true) : n));
}

/**
 * O arranjo inicial de novo, com as tabelas da sala como abas que não ficam à vista. As janelas
 * próprias continuam abertas, com o que têm: o pedaço de cada uma fica junto do chat (é ali que ele
 * aparece quando a janela fechar), e o que está nelas não se repete na mesa. O espaço do chat fica com
 * o id de antes, e as janelas que voltam para ele o acham. `wide`: a janela é larga (veja
 * `nextToChats`).
 */
export function resetLayout(layout: DesktopLayout, tableIds: readonly string[], wide = true): DesktopLayout {
  // as janelas de fora (as de dentro vão junto, no pedaço delas)
  const roots: DockNode[] = [];
  const walk = (n: DockNode) => {
    if (n.out) roots.push(n);
    else if (n.kind === 'group') n.children.forEach(walk);
  };
  for (const place of PLACES) {
    const site = layout.sites[place];
    if (site) walk(site.node);
  }
  const under = (n: DockNode): Sheet[] => (n.kind === 'content' ? n.sheets : n.children.flatMap(under));
  const key = (s: Sheet) => `${s.classId}|${s.param ?? ''}`;
  const windowed = new Set(roots.flatMap(under).filter((s) => s.classId !== 'system/mainChat').map(key));
  const chatId = chatPanel(layout)?.id;
  let next = mapSitesRaw(defaultLayout(wide), (n) => {
    if (n.kind !== 'content' || !n.sheets.length) return n;
    if (n.sheets.some((s) => s.main)) return chatId ? { ...n, id: chatId } : n;
    const sheets = n.sheets.filter((s) => !windowed.has(key(s)));
    return sheets.length ? { ...n, sheets, active: sheets[0]!.id } : null;
  });
  for (const root of roots) next = nextToChats(next, root, wide);
  return tableIds.reduce((l, id) => openTab(l, 'system/tableDock', id, { activate: false }), next);
}

/* ------------------------------------------------------------------ as operações, da mesa e das janelas */

/**
 * O que muda o layout, em forma de mensagem: a mesa e as janelas próprias desenham os espaços do
 * mesmo jeito (`room/Desk.tsx`), e a janela própria não grava o layout: manda a operação para a
 * principal (`room/windows.ts`), que a aplica (`applyLayoutOp`). Nas que levam uma aba (ou um
 * espaço) para dentro de outro, `before` é a aba que fica depois dela na faixa (null: no fim).
 */
export type LayoutOp =
  | { op: 'activate'; nodeId: string; sheetId: string }
  | { op: 'collapse'; nodeId: string; collapsed: boolean }
  | { op: 'sizes'; groupId: string; index: number; delta: number }
  | { op: 'resizeSite'; place: DockPlace; size: number }
  | { op: 'closeSheet'; sheetId: string }
  | { op: 'closePanel'; nodeId: string }
  | { op: 'moveSheet'; sheetId: string; to: SheetTarget; before?: string | null }
  | { op: 'movePanel'; nodeId: string; to: SheetTarget; before?: string | null }
  | { op: 'cloneSheet'; sheetId: string; to: SheetTarget; before?: string | null }
  | { op: 'movePanelTo'; nodeId: string; side: PanelSide }
  | { op: 'openInto'; classId: ModuleClassId; param?: string; nodeId: string }
  | { op: 'place'; classId: ModuleClassId; param?: string; to: DropTarget }
  | { op: 'popTab'; sheetId: string; win: string }
  | { op: 'popPanel'; nodeId: string; win: string };

/**
 * O destino com a posição na faixa: `before` é a aba que fica depois da arrastada (null: no fim).
 * `sheetId`: a aba arrastada, que não conta (vazio, com o espaço inteiro, todas contam).
 */
export function withIndex(layout: DesktopLayout, to: SheetTarget, before: string | null | undefined, sheetId: string): SheetTarget {
  if (to.kind !== 'into' || before === undefined) return to;
  const rest = findContent(layout, to.nodeId)?.sheets.filter((s) => s.id !== sheetId) ?? [];
  const index = before ? rest.findIndex((s) => s.id === before) : -1;
  return { ...to, index: index >= 0 ? index : rest.length };
}

export function applyLayoutOp(layout: DesktopLayout, op: LayoutOp): DesktopLayout {
  switch (op.op) {
    case 'activate':
      return activateSheet(layout, op.nodeId, op.sheetId);
    case 'collapse':
      return setCollapsed(layout, op.nodeId, op.collapsed);
    case 'sizes':
      return setGroupSizes(layout, op.groupId, op.index, op.delta);
    case 'resizeSite':
      return resizeSite(layout, op.place, op.size);
    case 'closeSheet':
      return closeSheet(layout, op.sheetId);
    case 'closePanel':
      return closePanel(layout, op.nodeId);
    case 'moveSheet':
      return moveSheet(layout, op.sheetId, withIndex(layout, op.to, op.before, op.sheetId));
    case 'movePanel':
      return movePanel(layout, op.nodeId, withIndex(layout, op.to, op.before, ''));
    case 'cloneSheet':
      return cloneSheet(layout, op.sheetId, withIndex(layout, op.to, op.before, op.sheetId));
    case 'movePanelTo':
      return movePanelTo(layout, op.nodeId, op.side);
    case 'openInto':
      return openTab(layout, op.classId, op.param, { activate: true, into: op.nodeId });
    case 'place':
      return placeModule(layout, op.classId, op.to, op.param);
    case 'popTab':
      return popTab(layout, op.sheetId, op.win);
    case 'popPanel':
      return popPanel(layout, op.nodeId, op.win);
    default:
      return layout;
  }
}
